import axios, { AxiosError } from 'axios'
import { reactive } from 'vue'
import router from './router'

// Same-origin in prod, Vite proxy in dev — relative URLs work for both.
export const http = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

// Reactive auth state. Components read this; the API client mutates it.
export interface User {
  id: string
  username: string
  full_name: string | null
}
export const auth = reactive<{ user: User | null; checked: boolean }>({
  user: null,
  checked: false,    // set true after the first /me probe so guards know
})

// 401 anywhere → drop session + bounce to /login (unless we're already there).
http.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      auth.user = null
      if (router.currentRoute.value.name !== 'login') {
        router.push({ name: 'login' })
      }
    }
    return Promise.reject(err)
  },
)

export async function loadCurrentUser(): Promise<void> {
  try {
    const { data } = await http.get<User>('/auth/me')
    auth.user = data
  } catch {
    auth.user = null
  } finally {
    auth.checked = true
  }
}

export async function login(username: string, password: string): Promise<void> {
  const { data } = await http.post<User>('/auth/login', { username, password })
  auth.user = data
}

export async function logout(): Promise<void> {
  await http.post('/auth/logout')
  auth.user = null
}

// ---- Measurements ----

export interface Measurement {
  id: string
  patient_id: string | null
  patient_name: string | null
  device_id: string
  timestamp: string
  duration_s: number | null
  voided_volume_ml: number | null
  max_flow_ml_s: number | null
  avg_flow_ml_s: number | null
  time_to_max_s: number | null
  battery_voltage: number | null
  created_at: string
}

export async function listMeasurements(): Promise<Measurement[]> {
  const { data } = await http.get<Measurement[]>('/measurements')
  return data
}

export async function assignPatient(
  measurementId: string,
  patientId: string | null,
): Promise<Measurement> {
  const { data } = await http.patch<Measurement>(
    `/measurements/${measurementId}`,
    { patient_id: patientId },
  )
  return data
}

// ---- Patients ----

export interface Patient {
  id: string
  name: string
  date_of_birth: string | null
  notes: string | null
  created_at: string
}

export async function listPatients(): Promise<Patient[]> {
  const { data } = await http.get<Patient[]>('/patients')
  return data
}

export async function createPatient(input: {
  name: string
  date_of_birth?: string | null
  notes?: string | null
}): Promise<Patient> {
  const { data } = await http.post<Patient>('/patients', input)
  return data
}
